import type { FundingRateMatrixExchange, FundingRateMatrixRow, FundingRateRow } from './FundingRateBlotter.types';

export interface FundingRateMatrix {
  /** Exchange columns present across the whole snapshot, sorted by id. */
  exchanges: FundingRateMatrixExchange[];
  /** One row per asset, in the configured order. */
  rows: FundingRateMatrixRow[];
  /** Largest |fundingRatePct| across all cells — the heatmap normalization reference. */
  maxAbsRate: number;
}

const EMPTY_MATRIX: FundingRateMatrix = { exchanges: [], rows: [], maxAbsRate: 0 };

/**
 * Pivots the flat (asset, exchange) rows into the Matrix shape: assets as rows (in the order they
 * first appear, i.e. the configured order) and exchanges as columns (the union across all assets,
 * sorted by id). Cells with no data are simply absent from `ratesByExchange`. Also returns the
 * largest absolute rate for the heatmap scale. Exported for unit testing.
 */
export function buildFundingRateMatrix(rows: FundingRateRow[]): FundingRateMatrix {
  if (rows.length === 0) {
    return EMPTY_MATRIX;
  }

  const displayNameByExchange = new Map<string, string>();
  const rowByAsset = new Map<string, FundingRateMatrixRow>();
  const assetOrder: string[] = [];
  let maxAbsRate = 0;

  for (const row of rows) {
    if (!rowByAsset.has(row.cmAssetId)) {
      rowByAsset.set(row.cmAssetId, {
        id: row.cmAssetId,
        symbol: row.symbol,
        cmAssetId: row.cmAssetId,
        cellsByExchange: {},
      });
      assetOrder.push(row.cmAssetId);
    }

    // Empty row (asset with no available pairs) contributes an asset row but no cells.
    if (row.exchange == null) {
      continue;
    }

    if (!displayNameByExchange.has(row.exchange)) {
      displayNameByExchange.set(row.exchange, row.exchangeDisplayName ?? row.exchange);
    }
    rowByAsset.get(row.cmAssetId)!.cellsByExchange[row.exchange] = {
      rate: row.fundingRatePct,
      openInterestUsd: row.openInterestUsd,
    };
    if (row.fundingRatePct != null) {
      maxAbsRate = Math.max(maxAbsRate, Math.abs(row.fundingRatePct));
    }
  }

  const exchanges = Array.from(displayNameByExchange, ([id, displayName]) => ({ id, displayName })).toSorted((a, b) =>
    a.id.localeCompare(b.id)
  );

  return { exchanges, rows: assetOrder.map(id => rowByAsset.get(id)!), maxAbsRate };
}
